import { lazy, Suspense } from 'react';
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Check, Copy, Edit, Save, X } from "lucide-react";
import { sanitizeHtml } from '@/src/utils/sanitization';
import { Loading } from '../shared';
import { OverviewSectionCard } from '@/components/shared/OverviewSectionCard';

const ReactQuill = lazy(() => import('react-quill-new'));

interface SectionLike {
  id: string;
  title: string;
  content: string;
}

interface MatterMemorySectionProps {
  section: SectionLike;
  index: number;
  isCollapsed: boolean;
  isEditing: boolean;
  canEdit: boolean;
  copiedSection: string | null;
  editedHtml: string;
  savingSummary: boolean;
  regeneratingSummary: boolean;
  isGenerating: boolean;
  onToggleCollapse: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onCopy: () => void;
  onShowEditWarning: () => void;
  onEditHtmlChange: (content: string) => void;
  renderSectionHtml: (content: string) => string;
}

export function MatterMemorySection({
  section,
  index,
  isCollapsed,
  isEditing,
  canEdit,
  copiedSection,
  editedHtml,
  savingSummary,
  regeneratingSummary,
  isGenerating,
  onToggleCollapse,
  onCancelEdit,
  onSave,
  onCopy,
  onShowEditWarning,
  onEditHtmlChange,
  renderSectionHtml,
}: MatterMemorySectionProps) {
  const displayTitle = index === 0 ? 'Key Facts' : section.title;

  const headerActions = !isCollapsed ? (
    <div className="flex gap-2">
      {isEditing ? (
        <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancelEdit}
            disabled={savingSummary}
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={onSave}
            disabled={savingSummary}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {savingSummary ? (
              <Loading text='Saving' inline size='sm' />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </>
      ) : (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCopy}
            disabled={regeneratingSummary}
            className="text-slate-600 hover:text-slate-900"
          >
            {copiedSection === section.id ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </>
            )}
          </Button>
          {canEdit && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={onShowEditWarning}
                      disabled={regeneratingSummary || isGenerating}
                      className="text-slate-600 hover:text-slate-900"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  </span>
                </TooltipTrigger>
                {(regeneratingSummary || isGenerating) && (
                  <TooltipContent>
                    <p>Editing is disabled while the overview is being generated</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
        </>
      )}
    </div>
  ) : undefined;

  const content = isEditing ? (
    <div className="border rounded-lg overflow-hidden">
      <Suspense fallback={<Loading />}>
        <ReactQuill
          value={editedHtml}
          onChange={onEditHtmlChange}
          theme="snow"
          modules={{
            toolbar: [
              [{ 'header': [3, 4, 5, 6, false] }],
              ['bold', 'italic', 'underline'],
              [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
              ['link'],
              ['clean']
            ],
            clipboard: {
              matchVisual: false
            }
          }}
          formats={[
            'header',
            'bold', 'italic', 'underline',
            'list', 'bullet', 'indent',
            'link'
          ]}
          style={{ height: '450px' }}
        />
      </Suspense>
    </div>
  ) : (
    <div
      className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-headings:font-semibold prose-p:text-slate-700 prose-p:leading-relaxed prose-li:text-slate-700 prose-strong:text-slate-900 prose-strong:font-semibold"
      dangerouslySetInnerHTML={{
        __html: sanitizeHtml(renderSectionHtml(section.content))
      }}
    />
  );

  return (
    <OverviewSectionCard
      id={section.id}
      number={index + 1}
      title={displayTitle}
      collapsed={isCollapsed}
      onToggle={onToggleCollapse}
      disableToggle={isEditing}
      indicatorStyle="up-down"
      headerActions={headerActions}
    >
      {content}
    </OverviewSectionCard>
  );
}
